'use client'

import React, { useRef, PropsWithChildren, ReactNode } from 'react'
import { motion, Variants, useInView } from 'motion/react'

interface RevealHeadingProps extends PropsWithChildren {
  enabled?: boolean
  threshold?: number
  delay?: number
}

interface ChildElement {
  children?: ReactNode;
  [key: string]: any;
}

const RevealHeading: React.FC<RevealHeadingProps> = ({ 
  children, 
  enabled = true,
  threshold = 0.1,
  delay = 0
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const isInView = useInView(containerRef, { 
    once: true, 
    amount: threshold 
  })

  // Define variants for word animation
  const wordVariants: Variants = {
    hidden: { 
      opacity: 0,
      y: 5,
    },
    visible: { 
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
        delay: delay
      }
    }
  }

  const splitTextIntoWords = (text: string) => {
    return text.split(/\s+/).map((word, index) => (
      <motion.span
        key={index}
        variants={wordVariants}
        initial="hidden"
        animate={isInView ? "visible" : "hidden"}
        className="reveal-word inline-block me-[0.2em] last:me-0"
        aria-hidden="true"
      >
        {word}
      </motion.span>
    ))
  }

  if (!enabled) return <>{children}</>

  return (
    <motion.div 
      ref={containerRef} 
      className="relative"
    >
      {React.Children.map(children, (child) => {
        if (React.isValidElement<ChildElement>(child)) {
          return React.cloneElement(child, {
            ...child.props as ChildElement,
            children: (
              <>
                <span className="sr-only">{child.props.children}</span>
                {typeof child.props.children === 'string' 
                  ? splitTextIntoWords(child.props.children as string)
                  : child.props.children}
              </>
            )
          })
        }
        return child
      })}
    </motion.div>
  )
}

export default RevealHeading